import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { include } from "@omni/utils/object.utils";
import { cn } from "@omni/utils/styles";
import { ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { useAddressBook } from "@/components/Checkout/CheckoutCustomer/addresses/context/addressBook.context";
import { useAddresses } from "@/components/Checkout/CheckoutCustomer/addresses/hooks/useAddresses.hook";
import { CheckoutCustomerFormValues } from "@/components/Checkout/CheckoutCustomer/checkoutCustomer.schema";
import { CheckBoxField } from "@/components/Form/RHF/FormFields/CheckboxField/CheckBoxField.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CheckoutCustomerDetailsFormModule from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.module";
import * as CheckoutCustomerDetailsFormSelectors from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Props = {
    className?: string;
};

export const SamePaymentAddress = ({ className }: Props) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const { addresses } = useAddresses();

    const { openAddressList, openAddressForm } = useAddressBook();

    const { reset } = useFormContext<CheckoutCustomerFormValues>();

    const deliveryAddressId = useSelector(
        CheckoutCustomerDetailsFormSelectors.deliveryAddressId
    );

    const handleOnChange = (
        event: ChangeEvent<HTMLInputElement>,
        onChange: (event: ChangeEvent<HTMLInputElement>) => void
    ) => {
        const isChecked = event.target.checked;

        if (isChecked) {
            if (isDefined(deliveryAddressId)) {
                const address = addresses.find(
                    ({ id }) => id === deliveryAddressId
                );

                dispatch(
                    CheckoutCustomerDetailsFormModule.update({
                        deliveryAddressId: deliveryAddressId,
                        paymentAddressId: deliveryAddressId
                    })
                );

                if (!address) return;

                reset((prev) => ({
                    ...prev,
                    delivery: address,
                    payment: undefined,
                    samePaymentAddress: true
                }));
            }

            onChange(event);
        } else if (addresses.length > 0) {
            event.preventDefault();

            if (addresses.length < 2) {
                openAddressForm({ type: AddressType.Payment });
            } else {
                openAddressList({
                    type: AddressType.Payment,
                    ...include(deliveryAddressId, (id) => ({
                        disabledIds: [id]
                    }))
                });
            }
        } else {
            onChange(event);
        }
    };

    return (
        <section className={cn(className)}>
            <Row>
                <Col>
                    <CheckBoxField<CheckoutCustomerFormValues>
                        name={"samePaymentAddress"}
                        label={t(
                            "checkout.customerDetails.samePaymentAddress.label"
                        )}
                        required={false}
                        optionalIndication={false}
                        onChange={handleOnChange}
                    />
                </Col>
            </Row>
        </section>
    );
};
